import styles from "./CancelEventDialog.module.scss";
import { useTranslation } from "../../i18n";
import { pickApiLabel } from "../../i18n/enumLabels";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { WarningIcon } from "../Icons/Warning";
import { useAlert } from "../../context/AlertContext";
import { fetcher } from "../../utils/fetcher";
import { useEnumStore } from "../../store/enumStore";
import { useEffect, useState } from "react";

interface Event {
  id: string;
  event_title?: string;
  [key: string]: any;
}

interface CancelEventDialogProps {
  event: Event | null;
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  onSuccessCallback?: () => void;
}

export const CancelEventDialog = ({
  event,
  dialogOpen,
  setDialogOpen,
  onSuccessCallback,
}: CancelEventDialogProps) => {
  const { t } = useTranslation();
  const { setAlert } = useAlert();
  const { cancellationReasons, fetchCancellationReasons } = useEnumStore();
  const [selectedReason, setSelectedReason] = useState<string>("");

  useEffect(() => {
    if (dialogOpen && cancellationReasons.length === 0) {
      fetchCancellationReasons();
    }
  }, [dialogOpen, cancellationReasons.length, fetchCancellationReasons]);

  const handleEventCancel = async () => {
    if (!event) return;

    if (!selectedReason) {
      setAlert(true, "warning", t("cancelEvent.alerts.pickReason"));
      return;
    }

    try {
      await fetcher({
        url: `/event/cancel-event/${event.id}?reason_of_cancellation=${selectedReason}`,
        method: "POST",
      });
      setAlert(
        true,
        "success",
        t("cancelEvent.alerts.cancelled"),
      );
      if (onSuccessCallback) {
        onSuccessCallback();
      }
    } catch (error) {
      setAlert(
        true,
        "error",
        t("cancelEvent.alerts.failed"),
      );
      console.error("Error canceling event:", error);
    } finally {
      setDialogOpen(false);
      setSelectedReason("");
    }
  };

  return (
    <>
      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setSelectedReason("");
        }}
        aria-labelledby="cancel-dialog-title"
        aria-describedby="cancel-dialog-description"
        maxWidth="xs"
        className={styles.dialog}
      >
        <DialogTitle id="cancel-dialog-title" className={styles.dialogTitle}>
          <WarningIcon />
          {t("cancelEvent.title")}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <DialogContentText id="cancel-dialog-description">
            {t("cancelEvent.text")}
          </DialogContentText>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="cancellation-reason-label">
              {t("cancelEvent.reason")}
            </InputLabel>
            <Select
              labelId="cancellation-reason-label"
              id="cancellation-reason-select"
              value={selectedReason}
              label={t("cancelEvent.reason")}
              onChange={(e) => setSelectedReason(e.target.value)}
            >
              {cancellationReasons.map((reason) => (
                <MenuItem key={reason.id} value={reason.value}>
                  {pickApiLabel(reason.label_cz, reason.label_en)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button
            variant="outlined"
            color="inherit"
            fullWidth
            onClick={() => {
              setDialogOpen(false);
              setSelectedReason("");
            }}
          >
            {t("cancelEvent.no")}
          </Button>
          <Button
            variant="contained"
            fullWidth
            color="error"
            disabled={!selectedReason}
            onClick={() => handleEventCancel()}
          >
            {t("cancelEvent.yes")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
